import CircularProgress from "@material-ui/core/CircularProgress";
import { LabelStyled } from "components/form/inputs/inputs.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { H3, Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  CalculationType,
  PayoutType,
  TipFilter,
  TipStatus,
  WorkerStatus,
} from "model/Tip";
import moment, { Moment } from "moment";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { companyCredits } from "services/api/company/companyCredits.service";
import { formatNumber } from "utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTipStore } from "web-apps/company/stores/tipStore/tipStore";
import { CircularProgressWrapper } from "../../calendar/weeklyCalendar.styled";
import { SelectText } from "../../timeReport/timeReports.styled";
import type { FocusedInputShape } from "./components/addTipSection";
import { AddTipSection } from "./components/addTipSection";
import { AlertModal } from "./components/alertModal";
import { CalculateModal } from "./components/calculateModal";
import { FooterButtons } from "./components/footerButtons";
import { HeaderSection } from "./components/headerSection";
import { PayoutModal } from "./components/payoutModal.component";
import { TipTabs } from "./components/tipTabs";
import { TipFilters } from "./components/tipFilters";
import { TipList } from "./components/tipList";
import { WorkerStatusIndicator } from "./components/workerStatusIndicator";
import { CreditHistory } from "./TipCredit/CreditHistory";
import { MonthlyReport } from "./MonthlyReport/monthlyReport";
import {
  AlertText,
  FlexColCenterContainer,
  FlexColEndContainer,
  InfoCircle,
  SelectRow,
  SpaceBtwItemsCenter,
} from "./tips.styled";
import { getFilteredTips } from "./tipUtils";
import { Setting } from "model/Workgroup";

export const CompanyTipPayout = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;

  const haveTipModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: Setting) => s.key === "CompanyShowTipModule",
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const settingsLoaded = companyState.companySettings.length > 0;
  const shouldRedirect = settingsLoaded && !haveTipModule;

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment());
  const [showHelpInfo, setShowHelpInfo] = useState(false);
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [selectedTipIds, _setSelectedTipIds] = useState<number[]>([]);
  const [expandedTipIds, setExpandedTipIds] = useState<number[]>([]);
  const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
  const [tipAmountAlertShown, setTipAmountAlertShown] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [activeMainTab, setActiveMainTab] = useState<
    "tips" | "credit" | "monthly"
  >("tips");
  const [isManualPayoutModalOpen, setIsManualPayoutModalOpen] = useState(false);
  const [payoutType, setPayoutType] = useState<
    "swish" | "manual" | undefined
  >();
  const [tipInputValues, setTipInputValues] = useState<Record<number, string>>(
    {},
  );
  const [selectedTips, setSelectedTips] = useState<number[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<TipFilter>(
    TipFilter.All,
  );
  const [activeTab, setActiveTab] = useState<CalculationType>(
    CalculationType.Time,
  );
  const [remainingAmounts, setRemainingAmounts] = useState<
    Record<number, number>
  >({});
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  );
  const [workerStatuses, setWorkerStatuses] = useState<
    Record<string, Record<number, WorkerStatus | null>>
  >({});

  const [tipState, tipActions] = useTipStore();
  const { workGroups } = tipState;
  const tipsList = tipState.tips;
  const totalTipsAmount = tipsList.reduce((acc, tip) => acc + tip.amount, 0);
  const amountDebounceRef = useRef<Record<number, number>>({});
  const latestReqVersionRef = useRef<Record<number, number>>({});
  const [undoLoadingIds, setUndoLoadingIds] = useState<number[]>([]);

  useEffect(() => {
    if (!shouldRedirect) return;

    const params = new URLSearchParams(location.search);
    params.set("page", "Subscription");

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [shouldRedirect, history, location.pathname, location.search]);

  useEffect(() => {
    if (!haveTipModule) return;
    if (!companyId) return;

    fetchBalance();
    setIsLoading(false);
    setSelectedFilter(TipFilter.All);
    setIsMultiSelectActive(false);
    setSelectedTips([]);
    handleFetchTips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haveTipModule, companyId, currentDate]);

  useEffect(() => {
    if (!haveTipModule) return;
    setIsMultiSelectActive(false);
    setSelectedTips([]);
    setExpandedTipIds([]);
  }, [haveTipModule, selectedFilter]);

  useEffect(() => {
    return () => {
      Object.values(amountDebounceRef.current).forEach((id) =>
        window.clearTimeout(id),
      );
    };
  }, []);

  const fetchBalance = async (): Promise<void> => {
    if (!companyId) return;

    try {
      const res = await companyCredits.getCreditBalance(companyId);
      setCreditBalance(res.data.amount);
    } catch (e) {
      console.error("Could not fetch credit balance", e);
    }
  };

  const handleFetchTips = async (): Promise<void> => {
    if (!companyId) return;

    const selectedMonth = currentDate.format("YYYY-MM");
    await tipActions.fetchTips(companyId, selectedMonth);
  };

  const calculateAndOpenModal = async (
    tipIds: number[],
    type?: "swish" | "manual",
  ): Promise<void> => {
    if (!companyId || tipIds.length === 0) return;

    await tipActions.calculateTips(
      companyId,
      tipIds,
      Object.values(workGroups)
        .flat()
        .map((group) => ({
          id: group.workGroupId,
          percentage: group.percentage,
        })),
    );

    setSelectedTips(tipIds);
    setPayoutType(type);

    if (type == "manual" || type == "swish") {
      setIsManualPayoutModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleDelete = async (tipId: number): Promise<void> => {
    if (!companyId || loadingIds.includes(tipId)) return;

    setLoadingIds((prev) => [...prev, tipId]);

    try {
      await tipActions.deleteTip(companyId, tipId);
      setSelectedTips((prev) => prev.filter((id) => id !== tipId));
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== tipId));
    }
  };

  const filteredTips = getFilteredTips(tipsList, selectedFilter);

  useEffect(() => {
    if (!haveTipModule) return;

    const updatedStatuses: Record<
      string,
      Record<number, WorkerStatus | null>
    > = {};

    tipState.tips.forEach((tip) => {
      if (!updatedStatuses[tip.date]) {
        updatedStatuses[tip.date] = {};
      }

      tip.workers.forEach((worker) => {
        updatedStatuses[tip.date][worker.workerId] = worker.status ?? null;
      });
    });

    setWorkerStatuses(updatedStatuses);
  }, [haveTipModule, tipState.tips]);

  const getWorkerStatus = (workerId: number, date: string): JSX.Element => {
    const status = workerStatuses[date]?.[workerId] ?? null;
    return <WorkerStatusIndicator status={status} />;
  };

  const toggleSelectTip = (id: number): void => {
    setSelectedTips((prev) => {
      const isAlreadySelected = prev.includes(id);
      const updatedSelection = isAlreadySelected
        ? prev.filter((tipId) => tipId !== id)
        : [...prev, id];

      return updatedSelection;
    });
  };

  const updateRemainingAmounts = (
    updatedAmounts: Record<number, number>,
  ): void => {
    setRemainingAmounts(updatedAmounts);
  };

  const toggleDropdown = (tipId: number): void => {
    const isCurrentlyExpanded = expandedTipIds.includes(tipId);

    if (!isCurrentlyExpanded) {
      if (!tipState.tipDetails[tipId]) {
        tipActions.fetchTipDetails(companyId, tipId);
      }
      setExpandedTipIds((prevExpandedTipIds) => [...prevExpandedTipIds, tipId]);
    } else {
      setExpandedTipIds((prevExpandedTipIds) =>
        prevExpandedTipIds.filter((id) => id !== tipId),
      );
    }
  };

  if (shouldRedirect) return null;

  return (
    <AlertModal>
      {(openAlertModal) => {
        const handleAddTip = async (): Promise<void> => {
          if (!startDate || !endDate || !companyId) return;

          const fromDate = moment(startDate).format("YYYY-MM-DD");
          const toDate = moment(endDate).format("YYYY-MM-DD");

          try {
            const { duplicateDates, uniqueDates } =
              await tipActions.checkForDuplicateTipDates(
                companyId,
                fromDate,
                toDate,
              );

            const addTips = async (dates: string[]): Promise<void> => {
              if (dates.length === 0) return;

              await Promise.all(
                dates.map((date) => tipActions.addTip(companyId, date)),
              );

              const firstDate = moment(dates[0]);
              const monthOfFirstDate = firstDate.startOf("month");

              if (!monthOfFirstDate.isSame(currentDate, "month")) {
                setCurrentDate(monthOfFirstDate);
              }

              setStartDate(null);
              setEndDate(null);
            };

            if (duplicateDates.length > 0) {
              const formattedDuplicates = duplicateDates
                .map((date) => moment(date).format("DD/MM"))
                .join(", ");

              openAlertModal(
                <Text style={{ marginBottom: "0" }}>
                  <Text>{t("CompanyTip.FollowingDatesAlreadyExists")}</Text>
                  <Text>{formattedDuplicates}</Text>
                  <Text style={{ margin: "0" }}>
                    {t("CompanyTip.AddAnywaysOrOnlyNewDates")}{" "}
                  </Text>
                </Text>,
                true,
                async (close) => {
                  await addTips([...duplicateDates, ...uniqueDates]);
                  close();
                },
                t("CompanyTip.AddAnyway"),
                async (close) => {
                  await addTips(uniqueDates);
                  close();
                },
                t("CompanyTip.OnlyNewDates"),
              );

              return;
            }

            await addTips([...uniqueDates]);
          } catch (err) {
            console.error("Could not check for duplicate tip dates", err);
          }
        };

        const handleTipAmountChange = (
          tipId: number,
          newAmount: string,
          date: string,
          onConfirm: () => void,
          onCancel: () => void,
        ): void => {
          if (!/^\d*$/.test(newAmount) || !companyId) return;
          const parsedAmount = newAmount.trim() === "" ? 0 : Number(newAmount);
          if (!Number.isFinite(parsedAmount)) return;

          const tip = tipsList.find((t) => t.companyTipId === tipId);
          if (!tip) return;

          setRemainingAmounts((prev) => ({ ...prev, [tipId]: parsedAmount }));

          // Använder debounce för att undvika race conditions mellan tip amount
          // och remainingAmount
          const updateAmount = (): void => {
            const existing = amountDebounceRef.current[tipId];
            if (existing) window.clearTimeout(existing);

            amountDebounceRef.current[tipId] = window.setTimeout(async () => {
              const v = (latestReqVersionRef.current[tipId] ?? 0) + 1;
              latestReqVersionRef.current[tipId] = v;

              try {
                await tipActions.updateTipAmount(
                  companyId,
                  tipId,
                  parsedAmount,
                );

                if (latestReqVersionRef.current[tipId] !== v) return;

                onConfirm();
              } catch (e) {
                setRemainingAmounts((prev) => ({
                  ...prev,
                  [tipId]: tip.remainingAmount,
                }));
              }
            }, 200);
          };

          if (
            (tip.status === TipStatus.Saved || tip.status === TipStatus.Done) &&
            !tipAmountAlertShown
          ) {
            openAlertModal(
              t("CompanyTip.IfYouChangeTipAllAmountsWillReset"),
              true,
              async (close) => {
                setTipAmountAlertShown(true);
                updateAmount();
                close();
              },
              t("General.Ok"),
              (close) => {
                onCancel();
                close();
              },
            );
            return;
          }

          updateAmount();
        };

        const handlePayout = async (
          companyTipIds: number | number[],
          payoutType: PayoutType,
        ) => {
          if (!companyId) return;

          const ids = Array.isArray(companyTipIds)
            ? companyTipIds
            : [companyTipIds];

          const confirmationText =
            payoutType === PayoutType.Automatic
              ? t("CompanyTip.AreYouSureToPayout")
              : t("CompanyTip.AreYouSureToComplete");

          openAlertModal(confirmationText, true, async (close) => {
            if (payoutType === PayoutType.Automatic) {
              const selectedTipsData = tipState.tips.filter((tip) =>
                ids.includes(tip.companyTipId),
              );

              const totalTipAmount = selectedTipsData.reduce(
                (acc, tip) => acc + tip.amount,
                0,
              );

              if ((creditBalance ?? 0) < totalTipAmount) {
                const amountNeeded = totalTipAmount;
                const balance = creditBalance ?? 0;
                const missingAmount = amountNeeded - balance;

                const formattedAmountNeeded = formatNumber(amountNeeded);
                const formattedBalance = formatNumber(balance);
                const formattedMissingAmount = formatNumber(missingAmount);

                openAlertModal(
                  <FlexColCenterContainer>
                    <CustomIcon
                      name="alert"
                      color={Color.LighterYellow}
                      size="80px"
                    />
                    <AlertText>
                      {t("CompanyTip.NotEnoughCredit", {
                        formattedBalance,
                        formattedAmountNeeded,
                        formattedMissingAmount,
                      })}
                    </AlertText>
                  </FlexColCenterContainer>,
                  false,
                  (close) => close(),
                );
                return;
              }
            }

            const result = await tipActions.payoutTips(
              companyId,
              ids,
              payoutType,
            );

            if (!result.success) {
              if (result.errorCode === 2) {
                openAlertModal(
                  <FlexColCenterContainer>
                    <CustomIcon
                      name="alert"
                      color={Color.LighterYellow}
                      size="80px"
                    />
                    <AlertText>{t("CompanyTip.MissingSwishNumber")}</AlertText>
                  </FlexColCenterContainer>,
                  false,
                  (close) => close(),
                );
              } else {
                openAlertModal(
                  t("CompanyTip.UnexpectedError"),
                  false,
                  (close) => close(),
                );
              }
              return;
            }

            await fetchBalance();

            const successMessage =
              payoutType === PayoutType.Automatic
                ? t("CompanyTip.PaymentsStarted")
                : t("CompanyTip.TipCompletedManually");

            openAlertModal(successMessage, false, (close) => close());
          });
        };

        const handleUndoManualPayout = (tipId: number): void => {
          if (!companyId) return;
          if (undoLoadingIds.includes(tipId)) return;

          openAlertModal(
            t("CompanyTip.ConfirmUndoManualPayout"),
            true,
            async (closeConfirmModal) => {
              setUndoLoadingIds((prev) => [...prev, tipId]);

              try {
                await tipActions.cancelManualPayout(companyId, [tipId]);
                closeConfirmModal();

                openAlertModal(t("CompanyTip.UndoManualPayoutSuccess"));
              } catch (e) {
                closeConfirmModal();
                openAlertModal(t("CompanyTip.UnexpectedError"));
              } finally {
                setUndoLoadingIds((prev) => prev.filter((id) => id !== tipId));
              }
            },
            t("General.Ok"),
          );
        };

        return (
          <>
            {isLoading ? (
              <CircularProgressWrapper>
                <CircularProgress />
              </CircularProgressWrapper>
            ) : (
              <>
                <SpaceBtwItemsCenter>
                  <H3>
                    {activeMainTab === "tips" && t("CompanyTip.DistributeTip")}
                    {activeMainTab === "credit" && t("TipCredit.Transactions")}
                    {activeMainTab === "monthly" &&
                      t("MonthlyTipReport.MonthlyReports")}
                  </H3>
                  <FlexColEndContainer>
                    <Text
                      fontFamily={FontFamily.MontserratSemiBold}
                      style={{ margin: "0" }}
                    >
                      {t("TipCredit.CurrentCredit")} {creditBalance}
                      {t("CompanyTip.Kr")}
                    </Text>

                    <TipTabs
                      activeTab={activeMainTab}
                      setActiveTab={setActiveMainTab}
                    />
                  </FlexColEndContainer>
                </SpaceBtwItemsCenter>

                {activeMainTab === "credit" && <CreditHistory />}

                {activeMainTab === "monthly" && (
                  <MonthlyReport
                    tips={tipsList}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                  />
                )}

                {activeMainTab === "tips" && (
                  <>
                    <div style={{ display: "flex" }}>
                      <LabelStyled>{t("CompanyTip.EnterDate")}</LabelStyled>
                      <InfoCircle
                        onMouseEnter={() => setShowHelpInfo(true)}
                        onMouseLeave={() => setShowHelpInfo(false)}
                      >
                        <Text
                          fontFamily={FontFamily.LeagueSpartanBold}
                          color={Color.White}
                          padding
                          fontSize={FontSize.Small}
                        >
                          i
                        </Text>
                      </InfoCircle>
                    </div>
                    {showHelpInfo && (
                      <Text fontSize={FontSize.Small}>
                        {t("CompanyTip.HelpInfo")}
                      </Text>
                    )}

                    <AddTipSection
                      startDate={startDate}
                      endDate={endDate}
                      focusedInput={focusedInput}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      setFocusedInput={setFocusedInput}
                      onAddTips={handleAddTip}
                      currentDate={currentDate}
                    />

                    <Text
                      fontFamily={FontFamily.MontserratSemiBold}
                      style={{ margin: "0", padding: "0", marginLeft: "18px" }}
                    >
                      {t("CompanyTip.Filter")}
                    </Text>

                    <SpaceBtwItemsCenter>
                      <TipFilters
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                      />
                      <SelectRow>
                        <SelectText
                          fontFamily={FontFamily.MontserratSemiBold}
                          style={{ userSelect: "none" }}
                          onClick={() => {
                            setIsMultiSelectActive(!isMultiSelectActive);
                            setSelectedTips([]);
                          }}
                        >
                          {t("CompanyTip.SelectMultipleDays")}
                        </SelectText>
                        <CustomIcon
                          size="27px"
                          padding="2px"
                          name="checkmark"
                          color={Color.White}
                          backgroundColor={
                            isMultiSelectActive
                              ? Color.MidnightBlue
                              : Color.White
                          }
                          onClick={() => {
                            setIsMultiSelectActive(!isMultiSelectActive);
                            setSelectedTips([]);
                          }}
                          style={{
                            border: `2px solid ${Color.MidnightBlue}`,
                            flexShrink: 0,
                            marginLeft: "8px",
                            marginRight: "12px",
                          }}
                        />
                      </SelectRow>
                    </SpaceBtwItemsCenter>

                    <HeaderSection
                      currentDate={currentDate}
                      setCurrentDate={setCurrentDate}
                    />

                    {companyId && (
                      <TipList
                        tipState={tipState}
                        tips={filteredTips}
                        loadingIds={loadingIds}
                        selectedTips={selectedTips}
                        selectedTipIds={selectedTipIds}
                        expandedTipIds={expandedTipIds}
                        tipInputValues={tipInputValues}
                        remainingAmounts={remainingAmounts}
                        isMultiSelectActive={isMultiSelectActive}
                        handleDelete={handleDelete}
                        toggleDropdown={toggleDropdown}
                        selectedFilter={selectedFilter}
                        toggleSelectTip={toggleSelectTip}
                        getWorkerStatus={getWorkerStatus}
                        handlePayout={handlePayout}
                        handleUndoManualPayout={handleUndoManualPayout}
                        undoLoadingIds={undoLoadingIds}
                        openCalculateModal={(tipId) =>
                          calculateAndOpenModal([tipId])
                        }
                        handleTipAmountChange={handleTipAmountChange}
                        setTipInputValues={setTipInputValues}
                      />
                    )}
                    {selectedFilter === TipFilter.All && (
                      <Text
                        style={{ display: "flex", justifyContent: "flex-end" }}
                        fontSize={FontSize.Large}
                        fontFamily={FontFamily.MontserratSemiBold}
                      >
                        {t("CompanyTip.TotalTipForMonth")}
                        {formatNumber(totalTipsAmount)} {t("CompanyTip.Kr")}
                      </Text>
                    )}

                    <FooterButtons
                      tipsList={tipsList}
                      selectedTips={selectedTips}
                      selectedFilter={selectedFilter}
                      onPayout={handlePayout}
                      calculateAndOpenModal={calculateAndOpenModal}
                      setIsManualPayoutModalOpen={setIsManualPayoutModalOpen}
                      setPayoutType={setPayoutType}
                    />

                    {companyId && (
                      <CalculateModal
                        open={isModalOpen}
                        activeTab={activeTab}
                        companyId={companyId}
                        setActiveTab={setActiveTab}
                        selectedTipIds={selectedTips}
                        onSaveSuccess={handleFetchTips}
                        onClose={() => setIsModalOpen(false)}
                        calculationResult={tipState.calculationResult}
                        onUpdateRemainingAmount={updateRemainingAmounts}
                      />
                    )}
                    {companyId && isManualPayoutModalOpen && (
                      <PayoutModal
                        open={isManualPayoutModalOpen}
                        onPayout={handlePayout}
                        companyId={companyId}
                        selectedTipIds={selectedTips}
                        onClose={() => setIsManualPayoutModalOpen(false)}
                        type={payoutType}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        );
      }}
    </AlertModal>
  );
};
